import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { FileSearchIcon } from 'lucide-react';
interface Suspect {
  name: string;
  detail: string;
  image: string;
}
const suspects: Suspect[] = [{
  // Heavyset 58yo man, square heavy face, silver-grey slicked hair, weathered, calculating
  name: 'Frank Deluca, 58',
  detail: 'Next-door neighbor',
  image: 'https://cdn.magicpatterns.com/uploads/haZhKa8mwp4Z2xX4v5c8QR/Frank_Deluca.png'
}, {
  // Composed 41yo professional woman, dark hair pulled back, intense cool gaze
  name: 'Diane Mercer, 41',
  detail: "Susan's best friend",
  image: 'https://cdn.magicpatterns.com/uploads/teGJ3qEe6LKHnatvfGGufM/Diane_Mercer.png'
}, {
  // Rugged 45yo man, reddish-brown hair, stubble, rough toughened features
  name: 'Gary Stull, 45',
  detail: "Robert's brother",
  image: 'https://cdn.magicpatterns.com/uploads/i7eu4VP66VcbccdXt2y7Lk/Gary_Stull.png'
}, {
  // Angular 52yo authoritative man, dark hair greying at temples, commanding presence
  name: 'Neil Grady, 52',
  detail: 'Family pastor',
  image: 'https://cdn.magicpatterns.com/uploads/wjTHdUTvgpi2vLceEvoQT5/_Pastor_Neil_Grady.png'
}, {
  // Round-faced 35yo anxious woman, dark-blonde shoulder-length wavy hair, light eyes
  name: 'Carol Ashby, 35',
  detail: 'Former housekeeper',
  image: 'https://cdn.magicpatterns.com/uploads/2WjRWYSHLHBWWFHtbNaTZD/Carol_Ashby.png'
}];
function SuspectCard({
  suspect,
  index
}: {
  suspect: Suspect;
  index: number;
}) {
  return <div className="relative shrink-0 w-[180px] sm:w-[220px] group" data-id="element-258">
      {/* Bordó glow behind */}
      <div className="absolute -inset-3 bg-[#8b1a1a]/30 rounded-2xl blur-2xl opacity-60 group-hover:opacity-90 transition-opacity duration-500 pointer-events-none" data-id="element-259" />

      <div className="relative rounded-xl overflow-hidden border border-[#8b1a1a]/40 shadow-[0_10px_40px_rgba(0,0,0,0.7)] bg-[#0a0a0a]" data-id="element-260">
        {/* 1:2 portrait */}
        <div className="relative aspect-[1/2] overflow-hidden" data-id="element-261">
          <img src={suspect.image} alt={`Suspect: ${suspect.name}`} loading="lazy" className="w-full h-full object-cover filter grayscale contrast-125 brightness-90 group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700" data-id="element-262" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/20 to-transparent" data-id="element-263" />
          <div className="absolute inset-0 bg-noise mix-blend-overlay opacity-30" data-id="element-264" />

          {/* Suspect number stamp */}
          <div className="absolute top-3 left-3 font-typewriter text-xs tracking-widest text-[#ece3d0]/80 bg-[#0a0a0a]/60 border border-[#8b1a1a]/50 px-2 py-0.5 rounded" data-id="element-265">
            SUSPECT 0{index + 1}
          </div>

          {/* Name + relation */}
          <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4" data-id="element-266">
            <h3 className="font-typewriter text-[#ece3d0] text-sm sm:text-base leading-tight drop-shadow" data-id="element-267">
              {suspect.name}
            </h3>
            <p className="font-serif-body text-[#ece3d0]/70 text-xs sm:text-sm italic mt-0.5" data-id="element-268">
              — {suspect.detail}
            </p>
          </div>
        </div>
      </div>
    </div>;
}
export function Suspects() {
  // Duplicate the list for a seamless infinite left scroll
  const loop = [...suspects, ...suspects];
  return <section className="relative py-16 sm:py-24 bg-[#0a0a0a] overflow-hidden" data-id="element-269">
      {/* Section bordó glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1100px] h-[500px] max-w-[130vw] bg-[#8b1a1a]/15 rounded-full blur-[150px] pointer-events-none" data-id="element-270" />
      <div className="absolute inset-0 bg-noise opacity-10 mix-blend-overlay pointer-events-none" data-id="element-271" />

      <div className="relative z-10" data-id="element-272">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-10 sm:mb-14 px-4" data-id="element-273">
          <div className="inline-flex items-center space-x-2 text-[#8b1a1a] mb-3 sm:mb-4 bg-[#8b1a1a]/10 px-4 py-1.5 rounded-full border border-[#8b1a1a]/20 backdrop-blur-sm" data-id="element-274">
            <FileSearchIcon className="w-4 h-4" data-id="element-275" />
            <span className="font-typewriter text-xs sm:text-sm tracking-wider uppercase" data-id="element-276">
              The Vanishing of Maple Street
            </span>
          </div>
          <h2 className="font-typewriter text-3xl sm:text-5xl text-[#ece3d0] mb-3 drop-shadow-lg" data-id="element-277">
            Meet some of our suspects
          </h2>
          <p className="font-serif-body text-[#ece3d0]/70 text-base sm:text-lg max-w-2xl mx-auto" data-id="element-278">
            Five people. One disappearance. Every one of them is hiding
            something.
          </p>
        </motion.div>

        {/* Marquee */}
        <div className="relative group/marquee" data-id="element-279">
          {/* Edge fades */}
          <div className="absolute left-0 top-0 bottom-0 w-16 sm:w-32 bg-gradient-to-r from-[#0a0a0a] to-transparent z-20 pointer-events-none" data-id="element-280" />
          <div className="absolute right-0 top-0 bottom-0 w-16 sm:w-32 bg-gradient-to-l from-[#0a0a0a] to-transparent z-20 pointer-events-none" data-id="element-281" />

          <motion.div className="flex gap-6 sm:gap-10 w-max px-6" animate={{
          x: ['0%', '-50%']
        }} transition={{
          duration: 28,
          repeat: Infinity,
          ease: 'linear'
        }} style={{
          willChange: 'transform'
        }} data-id="element-282">
            {loop.map((suspect, i) => <SuspectCard key={i} suspect={suspect} index={i % suspects.length} data-id="element-283" />)}
          </motion.div>
        </div>
      </div>
    </section>;
}